import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  User, 
  Settings, 
  LogOut, 
  Heart, 
  Calendar, 
  BarChart3, 
  Building, 
  ChevronDown,
  Shield
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
// import { usePermissions } from '../hooks/usePermissions'
import { logger } from '../utils/logger'

export default function UserMenu() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout, isAuthenticated, hasRole } = useAuth()
  
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Don't render if not authenticated
  if (!isAuthenticated || !user || !user.email) {
    return null
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
      setIsOpen(false)
    } catch (error) {
      logger.error('Logout error:', error)
    }
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    setIsOpen(false)
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="flex items-center gap-4">
        <Link 
          to="/login" 
          className="text-gray-700 hover:text-primary-600 transition-colors"
        >
          {t('auth.login.title')}
        </Link>
        <Link 
          to="/business-register" 
          className="btn-primary"
        >
          {t('navigation.register')}
        </Link>
      </div>
    )
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'business':
        return <Building className="h-4 w-4" />
      case 'admin':
        return <Shield className="h-4 w-4" />
      default:
        return <User className="h-4 w-4" />
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'business':
        return t('userMenu.business')
      case 'admin':
        return t('userMenu.admin')
      default:
        return t('userMenu.consumer')
    }
  }

  const getMenuItems = () => {
    const items = []

    // Common items for all users
    items.push({
      label: t('userMenu.profile'),
      icon: <User className="h-4 w-4" />,
      onClick: () => handleNavigation('/profile?edit=true')
    })

    // Role-specific items
    if (hasRole('consumer')) {
      items.push(
        {
          label: t('userMenu.favorites'),
          icon: <Heart className="h-4 w-4" />,
          onClick: () => handleNavigation('/favorites')
        },
        {
          label: t('userMenu.bookings'),
          icon: <Calendar className="h-4 w-4" />,
          onClick: () => handleNavigation('/bookings')
        }
      )
    }

    if (hasRole('business')) {
      items.push(
        {
          label: t('userMenu.businessDashboard'),
          icon: <BarChart3 className="h-4 w-4" />,
          onClick: () => handleNavigation('/business-dashboard')
        },
        {
          label: t('userMenu.businessProfile'),
          icon: <Building className="h-4 w-4" />,
          onClick: () => handleNavigation('/business-profile?edit=true')
        },
        {
          label: t('userMenu.listings'),
          icon: <Building className="h-4 w-4" />,
          onClick: () => handleNavigation('/business-listings')
        }
      )
    }

    if (hasRole('admin')) {
      items.push(
        {
          label: t('userMenu.adminDashboard'),
          icon: <Shield className="h-4 w-4" />,
          onClick: () => handleNavigation('/admin-dashboard')
        }
      )
    }

    // Settings for all users
    items.push({
      label: t('userMenu.settings'),
      icon: <Settings className="h-4 w-4" />,
      onClick: () => handleNavigation('/settings')
    })

    return items
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label={t('userMenu.userMenu')}
      >
        <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <div className="hidden md:block text-right">
          <div className="text-sm font-medium text-gray-900">{user?.name || 'User'}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1">
            {getRoleIcon(user?.role)}
            {getRoleLabel(user?.role)}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-medium">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <div className="font-medium text-gray-900">{user.name || 'User'}</div>
                <div className="text-sm text-gray-500">{user.email || ''}</div>
                <div className="text-xs text-gray-400 flex items-center gap-1">
                  {getRoleIcon(user.role || 'consumer')}
                  {getRoleLabel(user.role || 'consumer')}
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {getMenuItems().map((item, index) => (
              <button
                key={index}
                onClick={item.onClick}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 py-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              {t('userMenu.logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
